import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

const TABLE_HEAD = { backgroundColor: '#003366', color: '#fff' }
const LOWEST_ROW = { backgroundColor: '#d4edda' }
const LOWEST_BADGE = { backgroundColor: '#c3e6cb', color: '#155724', fontSize: '11px' }
const LANE_ROW = { backgroundColor: '#fff8e6' }
const CARD_STYLE = {
  marginBottom: '1.75rem',
  borderRadius: '8px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
  overflow: 'hidden'
}
const CARD_HEADER_STYLE = { padding: '14px 18px' }
const CARD_BODY_STYLE = { padding: '16px 18px 18px' }

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function parseApiResponse(raw) {
  const d = parseJsonIfString(raw)
  if (d?.body != null) return parseJsonIfString(d.body)
  return d
}

function normalizeApiPayload(raw) {
  if (!raw || typeof raw !== 'object') return raw
  const obj = { ...raw }
  if (typeof obj.data === 'string') obj.data = parseJsonIfString(obj.data)
  return obj
}

function isApiSuccess(r) {
  if (!r || typeof r !== 'object') return false
  const s = r.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function extractExcelBase64(raw) {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t.startsWith('UEs')) return raw
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        return extractExcelBase64(JSON.parse(t))
      } catch {
        return null
      }
    }
    return null
  }
  if (typeof raw === 'object') {
    if (raw.body != null) return extractExcelBase64(raw.body)
    if (typeof raw.data === 'string') return extractExcelBase64(raw.data)
    if (raw.data != null && typeof raw.data === 'object') {
      const nested = extractExcelBase64(raw.data)
      if (nested) return nested
    }
    if (raw.file != null) return extractExcelBase64(raw.file)
    if (raw.base64 != null) return extractExcelBase64(raw.base64)
    if (raw.excel != null) return extractExcelBase64(raw.excel)
  }
  return null
}

function triggerXlsxDownloadFromBase64(base64, prefix = 'LRFQ-Comparison') {
  if (!base64 || typeof base64 !== 'string' || !base64.startsWith('UEs')) {
    throw new Error('Invalid Excel data received')
  }
  const chars = atob(base64)
  const bytes = new Uint8Array(chars.length)
  for (let i = 0; i < chars.length; i += 1) bytes[i] = chars.charCodeAt(i)
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
  link.download = `${prefix}-${ts}.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function pickDownloadUrl(r) {
  if (!r || typeof r !== 'object') return ''
  return (
    r.files?.[0]?.downloadUrl ||
    r.downloadUrl ||
    r.data?.downloadUrl ||
    r.data?.files?.[0]?.downloadUrl ||
    r.url ||
    r.data?.url ||
    ''
  )
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}/.test(str)) return str
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return d.toLocaleDateString()
}

function formatDateTime(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString()
}

function formatAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toFixed(2)
}

function capitalizeStatus(value) {
  if (!value) return '-'
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

const supplierKey = (s) =>
  String(s?.supplierMailId || s?.supplierName || '').toLowerCase()

const matCode = (m) => m?.sourceCode || m?.materialCode || ''
const matDesc = (m) => m?.sourceDescription || m?.materialDescription || ''

function getMaterialKey(material, index = 0) {
  if (material?.sno != null && material.sno !== '') return `sno:${material.sno}`
  if (material?.uniqueId) return `uid:${material.uniqueId}`
  const code = String(matCode(material)).trim()
  const desc = String(matDesc(material)).trim()
  if (code && code !== '-') return `code:${code}`
  if (desc) return `desc:${desc}`
  return `idx:${index}`
}

function getLanes(material) {
  if (Array.isArray(material?.boqItems) && material.boqItems.length > 0) return material.boqItems
  if (Array.isArray(material?.boq) && material.boq.length > 0) return material.boq
  return []
}

const hasLanes = (m) => getLanes(m).length > 0
const getLaneId = (l) => String(l?.uniqueId || l?.boq_id || l?._id || l?.id || '').trim()

function getLaneNo(lane, parentLineNo, index) {
  return lane?.sNo || lane?.boqNo || `${parentLineNo ?? ''}.${index + 1}`.replace(/^\./, '')
}

function laneRoute(lane) {
  return `${lane?.sourceCity || '-'} (${lane?.sourcePincode || '-'}) → ${
    lane?.destinationCity || '-'
  } (${lane?.destinationPincode || '-'})`
}

function laneTruck(lane) {
  const dims = [lane?.truckLengthFt, lane?.truckWidthFt, lane?.truckHeightFt]
  const hasDims = dims.some((d) => d != null && d !== '')
  return `${lane?.truckName || '-'} / ${lane?.truckBody || '-'} · ${
    lane?.truckLoadabilityMt || '-'
  }${hasDims ? ` · ${dims.map((d) => d ?? '-').join('×')} Ft` : ''}`
}

const laneQtyText = (lane) => {
  const q = lane?.quantity
  return q != null && q !== '' && q !== '-' ? `Qty ${q}` : 'Qty -'
}

function findLaneSupplierQuote(lane, supplierOrQuote) {
  const mail = supplierKey(supplierOrQuote)
  if (!mail) return null
  return (lane?.supplierQuotes || []).find((q) => supplierKey(q) === mail) || null
}

function findLaneInQuote(quote, lane, index = 0) {
  const list = Array.isArray(quote?.boq) ? quote.boq : []
  if (list.length === 0) return null
  const wantId = getLaneId(lane)
  if (wantId) {
    const byId = list.find((b) => getLaneId(b) === wantId)
    if (byId) return byId
  }
  const wantNo = String(lane?.sNo || lane?.boqNo || '').trim()
  if (wantNo) {
    const byNo = list.find((b) => String(b.sNo || b.boqNo || '').trim() === wantNo)
    if (byNo) return byNo
  }
  return list[index] || null
}

const amountsEqual = (a, b) => {
  const na = Number(a)
  const nb = Number(b)
  return Number.isFinite(na) && Number.isFinite(nb) && Math.abs(na - nb) < 0.01
}

function resolveLowest(material, field, map, index) {
  const api = Number(material?.[field])
  if (Number.isFinite(api) && api > 0) return api
  const v = map?.[getMaterialKey(material, index)]
  return v != null ? Number(v) : null
}

function isLowestTotalQuote(quote, material, map, index) {
  if (quote?.isLowestTotalValue === true) return true
  const v = Number(quote?.totalValue)
  const low = resolveLowest(material, 'lowestTotalValue', map, index)
  return v > 0 && low != null && amountsEqual(v, low)
}

function isLowestBasicQuote(quote, material, map, index) {
  if (quote?.isLowestBasicPrice === true) return true
  const v = Number(quote?.basicPrice)
  const low = resolveLowest(material, 'lowestBasicPrice', map, index)
  return v > 0 && low != null && amountsEqual(v, low)
}

function computeLowestMap(materialComparison, apiField, valueField) {
  const map = {}
  materialComparison.forEach((material, index) => {
    const api = Number(material?.[apiField])
    if (Number.isFinite(api) && api > 0) {
      map[getMaterialKey(material, index)] = api
      return
    }
    const quotes = (material.supplierQuotes || []).filter(
      (q) => q.hasQuote && Number(q[valueField]) > 0
    )
    if (quotes.length === 0) return
    map[getMaterialKey(material, index)] = Math.min(...quotes.map((q) => Number(q[valueField])))
  })
  return map
}

function computeRanks(material, field) {
  const quotes = (material.supplierQuotes || []).filter(
    (q) => q.hasQuote && Number(q[field]) > 0
  )
  const sorted = [...quotes].sort((a, b) => Number(a[field]) - Number(b[field]))
  const map = {}
  sorted.forEach((q, i) => {
    map[supplierKey(q)] = i + 1
  })
  return map
}

/**
 * List suppliers Rank 1 → Rank 2 → Rank 3 (price order), not name-wise.
 * Prefers API rank fields, else sorts by the price field ascending.
 */
function sortQuotesByRank(quotes, { rankField = 'basicPriceRank', priceField = 'basicPrice', rankMap = null } = {}) {
  const list = Array.isArray(quotes) ? [...quotes] : []
  const resolveRank = (q) => {
    const fromMap = rankMap ? Number(rankMap[supplierKey(q)]) : NaN
    if (Number.isFinite(fromMap) && fromMap > 0) return fromMap
    const fromQuote = Number(q?.[rankField])
    if (Number.isFinite(fromQuote) && fromQuote > 0) return fromQuote
    return NaN
  }
  return list.sort((a, b) => {
    const ra = resolveRank(a)
    const rb = resolveRank(b)
    const hasRa = Number.isFinite(ra)
    const hasRb = Number.isFinite(rb)
    if (hasRa && hasRb && ra !== rb) return ra - rb
    if (hasRa && !hasRb) return -1
    if (!hasRa && hasRb) return 1
    const pa = Number(a?.[priceField])
    const pb = Number(b?.[priceField])
    if (Number.isFinite(pa) && Number.isFinite(pb) && pa > 0 && pb > 0 && pa !== pb) {
      return pa - pb
    }
    return 0
  })
}

const supplierNamesList = (list) =>
  !Array.isArray(list) || list.length === 0
    ? '-'
    : list.map((s) => s.supplierName || s.supplierMailId || '-').join(', ')

function buildSupplierMaterialGroups(suppliers, materialComparison) {
  return suppliers.map((supplier) => {
    const mail = supplierKey(supplier)
    const lines = materialComparison
      .map((material, materialIndex) => ({
        material,
        quote: (material.supplierQuotes || []).find((q) => supplierKey(q) === mail),
        materialIndex
      }))
      .filter((row) => row.quote?.hasQuote)
    return { supplier, lines }
  })
}

function getLowestQuoteForMaterial(material) {
  const quotes = (material.supplierQuotes || []).filter(
    (q) => q.hasQuote && Number(q.totalValue) > 0
  )
  if (quotes.length === 0) return null
  const flagged = quotes.filter((q) => q.isLowestTotalValue)
  if (flagged.length > 0) return flagged[0]
  return quotes.reduce((best, q) =>
    Number(q.totalValue) < Number(best.totalValue) ? q : best
  )
}

const buildMaterialOverallRows = (materialComparison) =>
  materialComparison
    .map((material) => ({ material, quote: getLowestQuoteForMaterial(material) }))
    .filter((row) => row.quote != null)

function getSupplierTermComments(term, supplier) {
  const mail = supplierKey(supplier)
  const entry = (term?.supplierTerms || []).find((r) => supplierKey(r) === mail)
  if (!entry) return '-'
  return entry.comments || entry.termsComments || '-'
}

const getHsn = (m, q) => q?.hsn || q?.hsnCode || m?.hsn || m?.hsnCode || '-'
const getUom = (m, q) => q?.uom || m?.uom || '-'
const getQty = (m, q) => {
  const v = q?.quantity ?? m?.quantity
  return v != null && v !== '' ? v : '-'
}

const RankBadge = ({ rank, isLowest }) => {
  if (rank == null || rank === '') return null
  return (
    <span
      className={`badge ml-1 ${isLowest ? 'badge-success' : 'badge-light'}`}
      style={{ fontSize: '10px' }}
    >
      #{rank}
    </span>
  )
}

const Legend = () => (
  <div
    className="d-flex flex-wrap align-items-center mb-3 p-2 rounded border"
    style={{ backgroundColor: '#f8f9fa', fontSize: '12px', gap: '12px' }}
  >
    <span className="text-muted fwt_600">Legend:</span>
    <span>
      <span className="badge badge-success mr-1">Lowest</span>
      Best price / rank #1
    </span>
    <span>
      <span
        className="d-inline-block mr-1"
        style={{ width: 14, height: 14, backgroundColor: '#d4edda', border: '1px solid #c3e6cb' }}
      />
      Highlighted row = lowest gross total
    </span>
  </div>
)

const SupplierFilter = ({ suppliers, value, onChange }) => {
  if (suppliers.length <= 1) return null
  return (
    <div className="mb-3">
      <label className="mb-1 d-block" style={{ fontSize: '12px', fontWeight: 600 }}>
        Filter by supplier
      </label>
      <select
        className="form-control form-control-sm"
        style={{ maxWidth: '320px' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="all">All suppliers</option>
        {suppliers.map((s) => (
          <option key={supplierKey(s)} value={supplierKey(s)}>
            {s.supplierName || s.supplierMailId}
          </option>
        ))}
      </select>
    </div>
  )
}

const COMMERCIAL_VIEW = {
  BY_SUPPLIER: 'bySupplier',
  BY_MATERIAL: 'byMaterial',
  MATERIAL_OVERALL: 'materialOverall'
}

const COMMERCIAL_VIEW_TABS = [
  { id: COMMERCIAL_VIEW.BY_SUPPLIER, label: 'By Supplier' },
  { id: COMMERCIAL_VIEW.BY_MATERIAL, label: 'Material Items' },
  { id: COMMERCIAL_VIEW.MATERIAL_OVERALL, label: 'Material Overall' }
]

const SectionNav = ({ active, onChange }) => (
  <ul className="nav nav-pills mb-3 flex-wrap" style={{ gap: '6px' }}>
    {[
      { id: 'overview', label: 'Overview', icon: 'fa-chart-pie' },
      { id: 'terms', label: 'Terms', icon: 'fa-list-check' },
      { id: 'commercial', label: 'Commercial', icon: 'fa-boxes-stacked' }
    ].map((tab) => (
      <li className="nav-item" key={tab.id}>
        <button
          type="button"
          className={`nav-link ${active === tab.id ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
          style={{ cursor: 'pointer', fontSize: '13px' }}
        >
          <i className={`fa-solid ${tab.icon} mr-1`} />
          {tab.label}
        </button>
      </li>
    ))}
  </ul>
)

const CommercialViewNav = ({ active, onChange }) => (
  <ul className="nav nav-pills flex-wrap" style={{ gap: '6px' }}>
    {COMMERCIAL_VIEW_TABS.map((tab) => (
      <li className="nav-item" key={tab.id}>
        <button
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={`nav-link ${active === tab.id ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
          style={{ cursor: 'pointer', fontSize: '13px' }}
        >
          {tab.label}
        </button>
      </li>
    ))}
  </ul>
)

const MaterialCell = ({ material }) => (
  <>
    <div className="fwt_600" style={{ fontSize: '12px' }}>{matCode(material) || '-'}</div>
    <div style={{ fontSize: '11px' }}>{matDesc(material) || '-'}</div>
  </>
)

const LaneCell = ({ lane }) => {
  const qty = lane?.quantity
  const hasQty = qty != null && qty !== '' && qty !== '-'
  return (
    <>
      <div className="fwt_600" style={{ fontSize: '12px' }}>{laneRoute(lane)}</div>
      <div className="text-muted" style={{ fontSize: '11px' }}>
        {laneTruck(lane)}
        {hasQty ? ` · Qty ${qty}` : ''}
      </div>
    </>
  )
}

/* ------------------------------ Material Overall ------------------------------ */
const MaterialOverallTable = ({ rows, currency }) => {
  const grossBasic = rows.reduce((s, r) => s + (Number(r.quote?.basicPrice) || 0), 0)
  const grossTotal = rows.reduce((s, r) => s + (Number(r.quote?.totalValue) || 0), 0)

  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr style={TABLE_HEAD}>
            <th style={{ fontSize: '12px' }}>#</th>
            <th style={{ fontSize: '12px' }}>Materials &amp; Lines</th>
            <th style={{ fontSize: '12px' }}>Supplier</th>
            <th style={{ fontSize: '12px' }}>HSN</th>
            <th style={{ fontSize: '12px' }}>UOM</th>
            <th style={{ fontSize: '12px' }} className="text-right">Qty</th>
            <th style={{ fontSize: '12px' }} className="text-right">
              Basic {currency ? `(${currency})` : ''}
            </th>
            <th style={{ fontSize: '12px' }} className="text-right">Total</th>
            <th style={{ fontSize: '12px' }}>Comments</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={9} className="text-center text-muted py-3">
                No lowest material quotes available
              </td>
            </tr>
          ) : (
            rows.flatMap(({ material, quote }, idx) => {
              const lineNo = material.lineNo != null ? material.lineNo : material.sno ?? idx + 1
              const lanes = getLanes(material)

              const parentRow = (
                <tr key={getMaterialKey(material, idx)} style={LOWEST_ROW}>
                  <td>
                    {lineNo}
                    {hasLanes(material) && (
                      <span className="badge badge-warning ml-1" style={{ fontSize: '9px' }}>
                        BOQ
                      </span>
                    )}
                  </td>
                  <td><MaterialCell material={material} /></td>
                  <td>
                    <div className="fwt_600" style={{ fontSize: '12px' }}>{quote.supplierName || '-'}</div>
                    <div className="text-muted" style={{ fontSize: '11px' }}>{quote.supplierMailId || '-'}</div>
                  </td>
                  <td style={{ fontSize: '12px' }}>{getHsn(material, quote)}</td>
                  <td style={{ fontSize: '12px' }}>{getUom(material, quote)}</td>
                  <td className="text-right" style={{ fontSize: '12px' }}>{getQty(material, quote)}</td>
                  <td className="text-right fwt_600">{formatAmount(quote.basicPrice)}</td>
                  <td className="text-right fwt_600">
                    {formatAmount(quote.totalValue)}
                    <span className="badge badge-success ml-1" style={{ fontSize: '10px' }}>Lowest</span>
                  </td>
                  <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                    {quote.comments || '-'}
                  </td>
                </tr>
              )

              const laneRows = lanes.map((lane, li) => {
                const laneQuote =
                  findLaneInQuote(quote, lane, li) || findLaneSupplierQuote(lane, quote)
                return (
                  <tr
                    key={`${getMaterialKey(material, idx)}-lane-${getLaneId(lane) || li}`}
                    style={LANE_ROW}
                  >
                    <td style={{ fontWeight: 600, color: '#856404', paddingLeft: '14px', fontSize: '12px' }}>
                      {getLaneNo(lane, lineNo, li)}
                    </td>
                    <td><LaneCell lane={lane} /></td>
                    <td className="text-muted" style={{ fontSize: '11px' }}>—</td>
                    <td className="text-center text-muted">—</td>
                    <td className="text-center text-muted">—</td>
                    <td className="text-right" style={{ fontSize: '12px' }}>
                      {lane.quantity != null && lane.quantity !== '' && lane.quantity !== '-'
                        ? lane.quantity
                        : '—'}
                    </td>
                    <td className="text-right">{formatAmount(laneQuote?.basicPrice ?? lane.basicPrice)}</td>
                    <td className="text-right">{laneQuote?.totalValue != null ? formatAmount(laneQuote.totalValue) : '—'}</td>
                    <td style={{ fontSize: '12px' }}>{laneQuote?.comments || '—'}</td>
                  </tr>
                )
              })

              return [parentRow, ...laneRows]
            })
          )}
          {rows.length > 0 && (
            <tr style={{ backgroundColor: '#f4f6f9' }}>
              <td colSpan={6} className="text-right fwt_600">
                Overall lowest total{currency ? ` (${currency})` : ''}
              </td>
              <td className="text-right fwt_600">{formatAmount(grossBasic)}</td>
              <td className="text-right fwt_600">{formatAmount(grossTotal)}</td>
              <td />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

 // Lane comparison (Material Items)
const LaneItemsComparisonTable = ({ material, currency, supplierFilter = 'all' }) => {
  const lanes = getLanes(material)
  if (lanes.length === 0) return null
  const lineNo = material.lineNo != null ? material.lineNo : material.sno

  return (
    <div className="mt-3">
      <div className="fwt_600 mb-2" style={{ fontSize: '13px' }}>
  <i className="fa-solid fa-list-ol mr-1 text-warning" />
  BOQ Line Items
</div>
      {lanes.map((lane, li) => {
        const laneNo = getLaneNo(lane, lineNo, li)
        const quotes = sortQuotesByRank(
          (lane.supplierQuotes || []).filter((q) => {
            if (!q.hasQuote) return false
            return supplierFilter === 'all' || supplierKey(q) === supplierFilter
          }),
          { rankField: 'basicPriceRank', priceField: 'basicPrice' }
        )
        const lowest = Number(lane.lowestBasicPrice)

        return (
          <div
            key={getLaneId(lane) || `${laneNo}-${li}`}
            className="mb-3 border rounded"
            style={{ backgroundColor: '#fffdf5' }}
          >
            <div
              className="px-2 py-1 border-bottom d-flex flex-wrap justify-content-between align-items-center"
              style={{ backgroundColor: '#fff3cd', fontSize: '12px', gap: '8px' }}
            >
             <div>
              <span className="fwt_600" style={{ color: '#856404' }}>{laneNo}</span>
              <span className="mx-2">·</span>
              <span className="fwt_600">{laneRoute(lane)}</span>
              <span className="text-muted ml-2">{laneTruck(lane)}</span>
              <span className="text-muted ml-2">· {laneQtyText(lane)}</span>
             </div>
              {Number.isFinite(lowest) && lowest > 0 && (
                <span className="badge badge-success">
                  Lowest basic: {formatAmount(lowest)}
                  {currency ? ` ${currency}` : ''}
                </span>
              )}
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead>
                  <tr style={TABLE_HEAD}>
                    <th style={{ fontSize: '12px' }}>Supplier</th>
                    <th style={{ fontSize: '12px' }} className="text-right">
                      Basic {currency ? `(${currency})` : ''}
                    </th>
                    <th style={{ fontSize: '12px' }}>Rank</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-2">No lane quotes</td>
                    </tr>
                  ) : (
                    quotes.map((q, qi) => (
                      <tr key={`${supplierKey(q)}-${qi}`} style={q.isLowestBasicPrice ? LOWEST_ROW : undefined}>
                        <td>
                          <div className="fwt_600" style={{ fontSize: '12px' }}>{q.supplierName || '-'}</div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>{q.supplierMailId || '-'}</div>
                        </td>
                        <td className="text-right fwt_600">
                          {formatAmount(q.basicPrice)}
                          {q.isLowestBasicPrice && (
                            <span className="badge badge-success ml-1" style={{ fontSize: '10px' }}>Lowest</span>
                          )}
                        </td>
                        <td>
                          <RankBadge rank={q.basicPriceRank} isLowest={q.isLowestBasicPrice} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const TermsPivotTable = ({ termsComparison, suppliers, supplierFilter }) => {
  const visible =
    supplierFilter === 'all'
      ? suppliers
      : suppliers.filter((s) => supplierKey(s) === supplierFilter)

  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr style={TABLE_HEAD}>
            <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
            <th style={{ fontSize: '12px', minWidth: '140px' }}>Terms</th>
            <th style={{ fontSize: '12px', minWidth: '160px' }}>Response</th>
            {visible.map((s) => (
              <th key={supplierKey(s)} style={{ fontSize: '12px', minWidth: '160px' }}>
                <div className="fwt_600">{s.supplierMailId || '-'}</div>
                {s.supplierName && (
                  <div style={{ fontSize: '10px', fontWeight: 400, opacity: 0.9 }}>{s.supplierName}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {termsComparison.length === 0 ? (
            <tr>
              <td colSpan={3 + visible.length} className="text-center text-muted py-3">No terms</td>
            </tr>
          ) : (
            termsComparison.map((term, ti) => (
              <tr key={`${term.sno ?? ti}-${term.rfqTerm || ti}`}>
                <td>{term.sno ?? ti + 1}</td>
                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '12px' }}>
                  {term.rfqTerm || '-'}
                </td>
                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                  {term.termsResponse || '-'}
                </td>
                {visible.map((s) => (
                  <td
                    key={`${term.sno ?? ti}-${supplierKey(s)}`}
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}
                  >
                    {getSupplierTermComments(term, s)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

//Material quote table                      
const MaterialQuoteTable = ({
  quotes,
  currency,
  material,
  materialIndex = 0,
  lowestTotalByMaterial,
  lowestBasicByMaterial,
  basicPriceRankMap,
  totalValueRankMap
}) => (
  <div className="table-responsive">
    <table className="table table-sm table-bordered mb-0">
      <thead>
        <tr style={TABLE_HEAD}>
          <th style={{ fontSize: '12px' }}>Supplier</th>
          <th style={{ fontSize: '12px' }}>HSN</th>
          <th style={{ fontSize: '12px' }}>UOM</th>
          <th style={{ fontSize: '12px' }} className="text-right">Qty</th>
          <th style={{ fontSize: '12px' }} className="text-right">Basic {currency ? `(${currency})` : ''}</th>
          <th style={{ fontSize: '12px' }} className="text-right">Total</th>
          <th style={{ fontSize: '12px' }}>Comments</th>
        </tr>
      </thead>
      <tbody>
        {quotes.length === 0 ? (
          <tr>
            <td colSpan={7} className="text-center text-muted py-3">No quotes for this material</td>
          </tr>
        ) : (
          quotes.map((quote, idx) => {
            const thisVal = Number(quote.totalValue)
            const thisBasic = Number(quote.basicPrice)
            const isLowest = isLowestTotalQuote(quote, material, lowestTotalByMaterial, materialIndex)
            const isLowestBasic = isLowestBasicQuote(quote, material, lowestBasicByMaterial, materialIndex)
            const basicRank = thisBasic > 0 ? basicPriceRankMap?.[supplierKey(quote)] ?? null : null
            const totalRank = thisVal > 0 ? totalValueRankMap?.[supplierKey(quote)] ?? null : null

            return (
              <tr key={idx} style={isLowest ? LOWEST_ROW : undefined}>
                <td>
                  <div className="fwt_600" style={{ fontSize: '13px' }}>{quote.supplierName || '-'}</div>
                  <div className="text-muted" style={{ fontSize: '11px' }}>{quote.supplierMailId || '-'}</div>
                </td>
                <td style={{ fontSize: '12px' }}>{getHsn(material, quote)}</td>
                <td style={{ fontSize: '12px' }}>{getUom(material, quote)}</td>
                <td className="text-right" style={{ fontSize: '12px' }}>{getQty(material, quote)}</td>
                <td className="text-right" style={isLowestBasic ? LOWEST_ROW : undefined}>
                  {formatAmount(quote.basicPrice)}
                  <RankBadge rank={basicRank} isLowest={isLowestBasic} />
                </td>
                <td className="text-right fwt_600">
                  {formatAmount(quote.totalValue)}
                  <RankBadge rank={totalRank} isLowest={isLowest} />
                  {isLowest && (
                    <span className="badge badge-success ml-1" style={{ fontSize: '10px' }}>Lowest</span>
                  )}
                </td>
                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                  {quote.comments || '-'}
                </td>
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  </div>
)

const LogisticsRfqComparison = () => {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [activeSection, setActiveSection] = useState('overview')
  const [materialView, setMaterialView] = useState(COMMERCIAL_VIEW.BY_SUPPLIER)
  const [supplierFilter, setSupplierFilter] = useState('all')
  const [expandedMaterials, setExpandedMaterials] = useState({})
  const [expandedSuppliers, setExpandedSuppliers] = useState({})
  const [isExporting, setIsExporting] = useState(false)
  const expandInitializedRef = useRef(false)

  const fetchComparison = useCallback(async () => {
    if (!id) {
      setLoadError('LRFQ ID is missing')
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setLoadError('')
    try {
     const response = await axios.post(API_URL, {
  action: 'get-lrfq-comparison-statement',
  lrfqid: id,
  currentUserId: localStorage.getItem('id')
})
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (isApiSuccess(responseData) && responseData.data) {
        setData(responseData.data)
      } else {
        const message = responseData?.message || 'Failed to fetch comparison statement'
        setLoadError(message)
        toast.error(message, { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Fetch LRFQ comparison error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const message = errData?.message || 'An error occurred while fetching comparison statement'
      setLoadError(message)
      toast.error(message, { autoClose: 2000 })
    } finally {
      setIsLoading(false)
    }
  }, [id])

  const exportComparisonExcel = async () => {
    if (!id || isExporting) return
    setIsExporting(true)
    try {
     const response = await axios.post(API_URL, {
  action: 'export-lrfq-comparison',
  lrfqid: id,
  currentUserId: localStorage.getItem('id')
})
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      const downloadUrl = pickDownloadUrl(responseData)
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer')
        toast.success('Comparison Excel export started', { autoClose: 2000 })
        return
      }

      const base64 = extractExcelBase64(responseData) || extractExcelBase64(response.data)
      if (!base64) {
        throw new Error(responseData?.message || 'Invalid Excel data received from server')
      }
      const prefix = String(data?.lrfqNumber || 'LRFQ-Comparison')
        .trim()
        .replace(/[^\w.-]+/g, '_')
      triggerXlsxDownloadFromBase64(base64, `${prefix}-comparison`)
      toast.success('Comparison Excel exported successfully', { autoClose: 2000 })
    } catch (error) {
      console.error('Export LRFQ comparison error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(
        (typeof errData === 'object' && errData?.message) ||
          error.message ||
          'Failed to export comparison Excel',
        { autoClose: 2500 }
      )
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    fetchComparison()
  }, [fetchComparison])

  useEffect(() => {
    expandInitializedRef.current = false
    setMaterialView(COMMERCIAL_VIEW.BY_SUPPLIER)
  }, [id])

  const suppliers = useMemo(() => (Array.isArray(data?.suppliers) ? data.suppliers : []), [data])
  const materialComparison = useMemo(
    () => (Array.isArray(data?.materialComparison) ? data.materialComparison : []),
    [data]
  )
  const termsComparison = useMemo(
    () => (Array.isArray(data?.termsComparison) ? data.termsComparison : []),
    [data]
  )
  const summary = data?.summary || {}
  const currency = data?.lrfqCurrency || data?.rfqCurrency || ''

  const lowestTotalByMaterial = useMemo(
    () => computeLowestMap(materialComparison, 'lowestTotalValue', 'totalValue'),
    [materialComparison]
  )
  const lowestBasicByMaterial = useMemo(
    () => computeLowestMap(materialComparison, 'lowestBasicPrice', 'basicPrice'),
    [materialComparison]
  )

  const rankedSuppliers = useMemo(() => {
    if (
      Array.isArray(summary.rankedSuppliersByGrossTotal) &&
      summary.rankedSuppliersByGrossTotal.length > 0
    ) {
      return summary.rankedSuppliersByGrossTotal
    }
    return [...suppliers].sort((a, b) => (a.grossTotalRank ?? 999) - (b.grossTotalRank ?? 999))
  }, [summary.rankedSuppliersByGrossTotal, suppliers])

  const supplierMaterialGroups = useMemo(
    () => buildSupplierMaterialGroups(suppliers, materialComparison),
    [suppliers, materialComparison]
  )

  const materialOverallRows = useMemo(() => {
    const rows = buildMaterialOverallRows(materialComparison)
    return supplierFilter === 'all'
      ? rows
      : rows.filter((row) => supplierKey(row.quote) === supplierFilter)
  }, [materialComparison, supplierFilter])

  useEffect(() => {
    if (expandInitializedRef.current) return
    if (materialComparison.length === 0 && suppliers.length === 0) return
    if (materialComparison.length > 0) {
      setExpandedMaterials({ [getMaterialKey(materialComparison[0], 0)]: true })
    }
    if (suppliers.length > 0) {
      setExpandedSuppliers({ [supplierKey(suppliers[0])]: true })
    }
    expandInitializedRef.current = true
  }, [materialComparison, suppliers])

  const commercialViewHelpText = useMemo(() => {
    if (materialView === COMMERCIAL_VIEW.BY_MATERIAL) {
      return 'Each material lists all supplier quotes vertically, with lane-wise quotes below.'
    }
    if (materialView === COMMERCIAL_VIEW.BY_SUPPLIER) {
      return 'Each supplier shows all material lines and lane rates, so you can review one vendor at a time.'
    }
    return 'Lowest total quote per material in one table, ideal for overall comparison.'
  }, [materialView])

  const toggleMaterial = (key) => setExpandedMaterials((p) => ({ ...p, [key]: !p[key] }))
  const toggleSupplierGroup = (key) => setExpandedSuppliers((p) => ({ ...p, [key]: !p[key] }))

  const expandAllMaterials = () => {
    const next = {}
    materialComparison.forEach((m, i) => {
      next[getMaterialKey(m, i)] = true
    })
    setExpandedMaterials(next)
  }
  const collapseAllMaterials = () => setExpandedMaterials({})

  const expandAllSuppliers = () => {
    const next = {}
    suppliers.forEach((s) => {
      next[supplierKey(s)] = true
    })
    setExpandedSuppliers(next)
  }
  const collapseAllSuppliers = () => setExpandedSuppliers({})

  /* ---------- By Material ---------- */
  const renderMaterialItemsView = () =>
    materialComparison.map((material, materialIndex) => {
      const cardKey = getMaterialKey(material, materialIndex)
      const isOpen = !!expandedMaterials[cardKey]
      const basicPriceRankMap = computeRanks(material, 'basicPrice')
      const totalValueRankMap = computeRanks(material, 'totalValue')
      const quotes = sortQuotesByRank(
        (material.supplierQuotes || []).filter((q) => {
          if (!q.hasQuote) return false
          return supplierFilter === 'all' || supplierKey(q) === supplierFilter
        }),
        {
          rankField: 'totalValueRank',
          priceField: 'totalValue',
          rankMap: totalValueRankMap
        }
      )

      return (
        <div key={cardKey} className="card border" style={CARD_STYLE}>
          <button
            type="button"
            className="card-header w-100 text-left d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer', backgroundColor: '#f4f6f9', border: 'none', ...CARD_HEADER_STYLE }}
            onClick={() => toggleMaterial(cardKey)}
          >
            <div className="pr-2">
              <span className="badge badge-secondary mr-2">
                #{material.lineNo ?? material.sno ?? materialIndex + 1}
              </span>
              {hasLanes(material) && (
                <span className="badge badge-warning mr-2" style={{ fontSize: '9px' }}>BOQ</span>
              )}
              <span className="fwt_600">{matCode(material) || '-'}</span>
              <span className="text-muted mx-2">·</span>
              <span style={{ fontSize: '13px' }}>{matDesc(material) || '-'}</span>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="badge mr-2" style={LOWEST_BADGE}>
                Lowest: {formatAmount(material.lowestTotalValue)} {currency}
              </span>
              <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} />
            </div>
          </button>
          {isOpen && (
            <div className="card-body" style={CARD_BODY_STYLE}>
              <div className="mb-3" style={{ fontSize: '12px' }}>
                <span className="text-muted">Lowest basic:</span>{' '}
                <strong>{formatAmount(material.lowestBasicPrice)} {currency}</strong>
                <span className="text-muted ml-2">
                  ({supplierNamesList(material.lowestBasicPriceSuppliers)})
                </span>
              </div>
              <MaterialQuoteTable
                quotes={quotes}
                currency={currency}
                material={material}
                materialIndex={materialIndex}
                lowestTotalByMaterial={lowestTotalByMaterial}
                lowestBasicByMaterial={lowestBasicByMaterial}
                basicPriceRankMap={basicPriceRankMap}
                totalValueRankMap={totalValueRankMap}
              />
              <LaneItemsComparisonTable
                material={material}
                currency={currency}
                supplierFilter={supplierFilter}
              />
            </div>
          )}
        </div>
      )
    })

// By Supplier
  const renderSupplierItemsView = () =>
    supplierMaterialGroups.map(({ supplier, lines }) => {
      const cardKey = supplierKey(supplier)
      if (supplierFilter !== 'all' && supplierFilter !== cardKey) return null
      const isOpen = !!expandedSuppliers[cardKey]

      return (
        <div key={cardKey} className="card border" style={CARD_STYLE}>
          <button
            type="button"
            className="card-header w-100 text-left d-flex justify-content-between align-items-center"
            style={{
              cursor: 'pointer',
              backgroundColor: supplier.isLowestGrossTotal ? '#d4edda' : '#f4f6f9',
              border: 'none',
              ...CARD_HEADER_STYLE
            }}
            onClick={() => toggleSupplierGroup(cardKey)}
          >
            <div>
              <span className="fwt_600">{supplier.supplierName || '-'}</span>
              {supplier.isLowestGrossTotal && <span className="badge badge-success ml-2">Lowest Total</span>}
              <div className="text-muted" style={{ fontSize: '11px' }}>
                {supplier.supplierMailId} · Rank #{supplier.grossTotalRank ?? '-'} · Total{' '}
                {formatAmount(supplier.grossTotalValue)} {currency}
              </div>
            </div>
            <div>
              <span className="badge badge-info mr-2">{lines.length} items</span>
              <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} />
            </div>
          </button>
          {isOpen && (
            <div className="card-body" style={CARD_BODY_STYLE}>
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr style={TABLE_HEAD}>
                      <th style={{ fontSize: '12px' }}>#</th>
                      <th style={{ fontSize: '12px' }}>Materials &amp; Lines</th>
                      <th style={{ fontSize: '12px' }}>HSN</th>
                      <th style={{ fontSize: '12px' }}>UOM</th>
                      <th style={{ fontSize: '12px' }} className="text-right">Qty</th>
                      <th style={{ fontSize: '12px' }} className="text-right">Basic</th>
                      <th style={{ fontSize: '12px' }} className="text-right">Total</th>
                      <th style={{ fontSize: '12px' }}>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-3">No material quotes</td>
                      </tr>
                    ) : (
                      lines.flatMap(({ material, quote, materialIndex }, idx) => {
                        const isLowest = isLowestTotalQuote(quote, material, lowestTotalByMaterial, materialIndex)
                        const lineNo = material.lineNo != null ? material.lineNo : material.sno ?? idx + 1
                        const lanes = getLanes(material)

                        const parentRow = (
                          <tr key={`sup-mat-${materialIndex}`} style={isLowest ? LOWEST_ROW : undefined}>
                            <td>
                              {lineNo}
                              {hasLanes(material) && (
                                <span className="badge badge-warning ml-1" style={{ fontSize: '9px' }}>BOQ</span>
                              )}
                            </td>
                            <td><MaterialCell material={material} /></td>
                            <td style={{ fontSize: '12px' }}>{getHsn(material, quote)}</td>
                            <td style={{ fontSize: '12px' }}>{getUom(material, quote)}</td>
                            <td className="text-right" style={{ fontSize: '12px' }}>{getQty(material, quote)}</td>
                            <td className="text-right fwt_600">{formatAmount(quote?.basicPrice)}</td>
                            <td className="text-right fwt_600">
                              {formatAmount(quote?.totalValue)}
                              {isLowest && (
                                <span className="badge badge-success ml-1" style={{ fontSize: '10px' }}>Lowest</span>
                              )}
                            </td>
                            <td style={{ fontSize: '12px' }}>{quote?.comments || '-'}</td>
                          </tr>
                        )

                        const laneRows = lanes.map((lane, li) => {
                          const fromQuote = findLaneInQuote(quote, lane, li)
                          const fromLane = findLaneSupplierQuote(lane, quote || {})
                          const basic = fromQuote?.basicPrice ?? fromLane?.basicPrice ?? null
                          const isLowestLane = Boolean(
                            fromLane?.isLowestBasicPrice ||
                              (Number.isFinite(Number(lane.lowestBasicPrice)) &&
                                Number(basic) > 0 &&
                                amountsEqual(basic, lane.lowestBasicPrice))
                          )
                          return (
                            <tr key={`sup-mat-${materialIndex}-lane-${getLaneId(lane) || li}`} style={LANE_ROW}>
                              <td style={{ fontWeight: 600, color: '#856404', paddingLeft: '14px', fontSize: '12px' }}>
                                {getLaneNo(lane, lineNo, li)}
                              </td>
                              <td><LaneCell lane={lane} /></td>
                              <td className="text-center text-muted">—</td>
                              <td className="text-center text-muted">—</td>
                              <td className="text-right" style={{ fontSize: '12px' }}>
                                {lane.quantity != null && lane.quantity !== '' && lane.quantity !== '-'
                                  ? lane.quantity
                                  : '—'}
                              </td>
                              <td className="text-right">
                                {formatAmount(basic)}
                                {isLowestLane && (
                                  <span className="badge badge-success ml-1" style={{ fontSize: '10px' }}>Lowest</span>
                                )}
                              </td>
                              <td className="text-right">
                                {fromQuote?.totalValue != null ? formatAmount(fromQuote.totalValue) : '—'}
                              </td>
                              <td style={{ fontSize: '12px' }}>{fromQuote?.comments || '—'}</td>
                            </tr>
                          )
                        })

                        return [parentRow, ...laneRows]
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )
    })

  const commercialViewContent = (() => {
    if (materialComparison.length === 0) return <p className="text-muted">No materials.</p>

    if (materialView === COMMERCIAL_VIEW.MATERIAL_OVERALL) {
      return (
        <div className="card border" style={CARD_STYLE}>
          <div className="card-header fwt_600" style={{ ...CARD_HEADER_STYLE, backgroundColor: '#f4f6f9' }}>
            <i className="fa-solid fa-trophy mr-2 text-success" />
            Lowest quote per material
            <span className="badge badge-success ml-2">{materialOverallRows.length} items</span>
          </div>
          <div className="card-body" style={CARD_BODY_STYLE}>
            <MaterialOverallTable rows={materialOverallRows} currency={currency} />
          </div>
        </div>
      )
    }
    if (materialView === COMMERCIAL_VIEW.BY_SUPPLIER) return renderSupplierItemsView()
    if (materialView === COMMERCIAL_VIEW.BY_MATERIAL) return renderMaterialItemsView()
    return null
  })()

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">LRFQ Comparison Statement</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to={`/logisticsRfqView/${id}`}>
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="frmwdth_cntr_rfq mx-auto">
              <div className="card card-primary card-outline">
                <div className="card-body text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="frmwdth_cntr_rfq mx-auto">
              <div className="card card-primary card-outline">
                <div className="card-body text-center py-5">
                  <p className="text-muted mb-0">{loadError || 'Comparison not found'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="frmwdth_cntr_rfq mx-auto">
            <div className="card card-primary card-outline">
              <div className="card-body">
                <div
                  className="d-flex flex-wrap justify-content-between align-items-start mb-3 pb-3 border-bottom"
                  style={{ gap: '12px' }}
                >
                  <div>
                    <h5 className="mb-1 fwt_600">{data.lrfqNumber || data.rfqNumber || '-'}</h5>
                    <div className="text-muted" style={{ fontSize: '13px' }}>
                      Due {formatDate(data.lrfqDueDate || data.rfqDueDate)} ·{' '}
                      {capitalizeStatus(data.lrfqStatus || data.rfqStatus)} · {currency || 'INR'}
                    </div>
                    {(data.lrfqSummary || data.rfqSummary) && (
                      <div className="mt-1" style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                        {data.lrfqSummary || data.rfqSummary}
                      </div>
                    )}
                  </div>
                  <div className="text-md-right" style={{ fontSize: '13px' }}>
                    <div className="mb-2">
                      <button
                        type="button"
                        className="btn btn-blue btn-sm"
                        onClick={exportComparisonExcel}
                        disabled={isExporting || !id}
                        title="Export comparison Excel"
                      >
                        {isExporting ? (
                          <>
                            <PulseLoader color="#fff" size={6} />
                            <span className="ml-2">Exporting...</span>
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-file-export mr-1" />
                            Export Excel
                          </>
                        )}
                      </button>
                    </div>
                    <div>
                      <span className="text-muted">LRFQ Date:</span>{' '}
                      {formatDate(data.lrfqDate || data.rfqDate)}
                    </div>
                    <div>
                      <span className="text-muted">Suppliers quoted:</span>{' '}
                      {summary.suppliersWithQuotes ?? '-'} / {summary.totalSuppliers ?? suppliers.length}
                    </div>
                  </div>
                </div>

                <Legend />
                <SectionNav active={activeSection} onChange={setActiveSection} />

                {activeSection === 'overview' && (
                  <>
                    <div className="row mb-4">
                      <div className="col-md-4 mb-2">
                        <div className="border rounded p-3 h-100 text-center" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="text-muted" style={{ fontSize: '12px' }}>Total Suppliers</div>
                          <div className="fwt_600" style={{ fontSize: '28px' }}>
                            {summary.totalSuppliers ?? suppliers.length}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <div className="border rounded p-3 h-100 text-center" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="text-muted" style={{ fontSize: '12px' }}>Quotes Received</div>
                          <div className="fwt_600" style={{ fontSize: '28px' }}>
                            {summary.suppliersWithQuotes ?? '-'}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <div
                          className="border rounded p-3 h-100 text-center"
                          style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb' }}
                        >
                          <div style={{ fontSize: '12px', color: '#155724' }}>Recommended (Lowest Total)</div>
                          <div className="fwt_600" style={{ fontSize: '16px', color: '#155724' }}>
                            {summary.lowestGrossTotalSupplier?.supplierName || '-'}
                          </div>
                          <div style={{ fontSize: '20px', color: '#155724' }} className="fwt_600">
                            {formatAmount(summary.lowestGrossTotalSupplier?.grossTotalValue)}{' '}
                            <small>{currency}</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h6 className="text-primary mb-2">
                      <i className="fa-solid fa-trophy mr-1" />
                      Supplier Ranking (by Gross Total)
                    </h6>
                    <p className="text-muted mb-2" style={{ fontSize: '12px' }}>
                      Rank #1 is the lowest overall quote. Compare all suppliers in one place.
                    </p>

                    {rankedSuppliers.length === 0 ? (
                      <p className="text-muted">No supplier quotes yet.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={TABLE_HEAD}>
                              <th style={{ fontSize: '12px', width: '56px' }}>Rank</th>
                              <th style={{ fontSize: '12px' }}>Supplier</th>
                              <th style={{ fontSize: '12px' }}>Email</th>
                              <th style={{ fontSize: '12px' }}>Submitted</th>
                              <th style={{ fontSize: '12px' }} className="text-right">Basic</th>
                              <th style={{ fontSize: '12px' }} className="text-right">Gross Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rankedSuppliers.map((row, index) => {
                              const meta = suppliers.find((s) => supplierKey(s) === supplierKey(row))
                              const isLowest = row.isLowestGrossTotal
                              return (
                                <tr key={index} style={isLowest ? LOWEST_ROW : undefined}>
                                  <td className="text-center fwt_600">{row.rank ?? index + 1}</td>
                                  <td>
                                    {row.supplierName || '-'}
                                    {isLowest && <span className="badge badge-success ml-1">Lowest</span>}
                                  </td>
                                  <td style={{ fontSize: '12px' }}>{row.supplierMailId || '-'}</td>
                                  <td style={{ fontSize: '12px' }}>
                                    {meta?.hasQuoteSubmitted ? (
                                      <>
                                        <span className="badge badge-success">Yes</span>
                                        <div className="text-muted mt-1">{formatDateTime(meta.quoteSubmittedAt)}</div>
                                      </>
                                    ) : (
                                      <span className="badge badge-secondary">No</span>
                                    )}
                                  </td>
                                  <td className="text-right">{formatAmount(row.grossTotalBasicPrice)}</td>
                                  <td className="text-right fwt_600">
                                    {formatAmount(row.grossTotalValue)} {currency}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {activeSection === 'commercial' && (
                  <>
                    <SupplierFilter suppliers={suppliers} value={supplierFilter} onChange={setSupplierFilter} />
                    <div
                      className="d-flex flex-wrap justify-content-between align-items-center mb-3"
                      style={{ gap: '10px' }}
                    >
                      <CommercialViewNav active={materialView} onChange={setMaterialView} />
                      {materialView === COMMERCIAL_VIEW.BY_MATERIAL ? (
                        <div className="btn-group btn-group-sm">
                          <button type="button" className="btn btn-outline-secondary" onClick={expandAllMaterials}>
                            Expand all
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={collapseAllMaterials}>
                            Collapse all
                          </button>
                        </div>
                      ) : materialView === COMMERCIAL_VIEW.BY_SUPPLIER ? (
                        <div className="btn-group btn-group-sm">
                          <button type="button" className="btn btn-outline-secondary" onClick={expandAllSuppliers}>
                            Expand all
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={collapseAllSuppliers}>
                            Collapse all
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
                      {commercialViewHelpText}
                    </p>

                    <div key={materialView}>{commercialViewContent}</div>
                  </>
                )}

                {activeSection === 'terms' && (
                  <>
                    <SupplierFilter suppliers={suppliers} value={supplierFilter} onChange={setSupplierFilter} />
                    <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
                      One row per term, with supplier emails as columns showing their comments.
                    </p>
                    <TermsPivotTable
                      termsComparison={termsComparison}
                      suppliers={suppliers}
                      supplierFilter={supplierFilter}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LogisticsRfqComparison